import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { DATE_FORMATS } from "@tether-web-portal/constants/dateFormats";
import { DayOfWeek, MonitoringHours } from "@tether-web-portal/types/MonitoringHours";

import { LocalParts, localiseTimestamp } from "./fastTz";

dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * A monitoring window resolved to the property's timezone. `startMinutes`
 * and `endMinutes` are minutes-of-day in that timezone. We keep the DoW
 * alongside so a bucket on Tuesday doesn't accidentally match Monday's hours.
 *
 * Windows that wrap past midnight (e.g. 22:00–06:00) are split into two
 * entries at the day boundary.
 */
interface ResolvedWindow {
  dayOfWeek: DayOfWeek;
  startMinutes: number;
  endMinutes: number;
}

const DAY_ORDER: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

const nextDay = (day: DayOfWeek): DayOfWeek =>
  DAY_ORDER[(DAY_ORDER.indexOf(day) + 1) % DAY_ORDER.length];

const parseWindowToMinutes = (
  window: MonitoringHours,
  propertyTimezone: string,
): ResolvedWindow[] => {
  const start = dayjs
    .tz(window.startTime, DATE_FORMATS.TIME_24_HOUR, window.timezone)
    .utc()
    .tz(propertyTimezone);
  const end = dayjs
    .tz(window.endTime, DATE_FORMATS.TIME_24_HOUR, window.timezone)
    .utc()
    .tz(propertyTimezone);

  const startMinutes = start.hour() * 60 + start.minute();
  const endMinutes = end.hour() * 60 + end.minute();

  if (endMinutes > startMinutes) {
    return [{ dayOfWeek: window.dayOfWeek, startMinutes, endMinutes }];
  }

  // Window wrapped past midnight: split at day boundary.
  return [
    { dayOfWeek: window.dayOfWeek, startMinutes, endMinutes: 24 * 60 },
    { dayOfWeek: nextDay(window.dayOfWeek), startMinutes: 0, endMinutes },
  ];
};

export const resolveMonitoringWindows = (
  monitoringHours: MonitoringHours[],
  propertyTimezone: string,
): ResolvedWindow[] =>
  monitoringHours.flatMap((w) => parseWindowToMinutes(w, propertyTimezone));

/**
 * Half-open interval overlap: [a0, a1) ∩ [b0, b1) ≠ ∅ iff a0 < b1 AND b0 < a1.
 */
const intervalsOverlap = (a0: number, a1: number, b0: number, b1: number): boolean =>
  a0 < b1 && b0 < a1;

interface BucketLike {
  startTimestamp: string;
  endTimestamp: string;
}

/**
 * Keep only the buckets that overlap at least one monitoring window. Uses
 * half-open [start, end) semantics; the API's endOf-rounded `xx:59:59.999`
 * is normalised to the following minute so boundary hits behave consistently.
 *
 * If `monitoringHours` is empty the property is treated as 24/7 — every
 * bucket is kept.
 */
export const filterToMonitoredBuckets = <T extends BucketLike>(
  buckets: T[],
  monitoringHours: MonitoringHours[],
  propertyTimezone: string,
): T[] => {
  if (!monitoringHours.length) return buckets;

  const windows = resolveMonitoringWindows(monitoringHours, propertyTimezone);
  const windowsByDay = new Map<DayOfWeek, ResolvedWindow[]>();
  for (const w of windows) {
    if (!windowsByDay.has(w.dayOfWeek)) windowsByDay.set(w.dayOfWeek, []);
    windowsByDay.get(w.dayOfWeek)!.push(w);
  }

  return buckets.filter((b) => bucketOverlapsAnyWindow(b, windowsByDay, propertyTimezone));
};

// Memoizes the +1ms-shifted LocalParts keyed by the ORIGINAL endTimestamp.
// Without this, every call here builds a fresh `endTimestamp + 1ms` ISO
// string and feeds it to the localPartsCache under a key that no other
// call site ever inserts — so each unique end-timestamp is a cold Intl
// parse. For a 100-room × 672-bucket build, 67k+ end lookups collapse to
// the 672 unique end-timestamps actually present in the data.
const endPartsCache = new Map<string, LocalParts>();
const END_CACHE_MAX = 200_000;

const localiseEndTimestamp = (iso: string, timezone: string): LocalParts => {
  const key = `${timezone}|${iso}`;
  const cached = endPartsCache.get(key);
  if (cached) return cached;
  // Normalise API endTimestamps like `xx:59:59.999` to the next whole
  // millisecond so boundary buckets aren't accidentally clipped off.
  const shifted = new Date(Date.parse(iso) + 1).toISOString();
  const parts = localiseTimestamp(shifted, timezone);
  if (endPartsCache.size >= END_CACHE_MAX) {
    const target = Math.floor(END_CACHE_MAX / 2);
    let toRemove = endPartsCache.size - target;
    for (const k of endPartsCache.keys()) {
      if (toRemove-- <= 0) break;
      endPartsCache.delete(k);
    }
  }
  endPartsCache.set(key, parts);
  return parts;
};

const bucketOverlapsAnyWindow = (
  bucket: BucketLike,
  windowsByDay: Map<DayOfWeek, ResolvedWindow[]>,
  propertyTimezone: string,
): boolean => {
  const startParts = localiseTimestamp(bucket.startTimestamp, propertyTimezone);
  const endParts = localiseEndTimestamp(bucket.endTimestamp, propertyTimezone);

  if (startParts.dateKey === endParts.dateKey) {
    const windows = windowsByDay.get(startParts.dayOfWeek);
    if (!windows) return false;
    const s = startParts.minutesOfDay;
    // If end falls exactly on midnight (minutesOfDay === 0) it means the
    // bucket runs to end-of-day; treat as 24*60 for the overlap check.
    const e = endParts.minutesOfDay === 0 ? 24 * 60 : endParts.minutesOfDay;
    return windows.some((w) => intervalsOverlap(s, e, w.startMinutes, w.endMinutes));
  }

  // Bucket spans midnight — check each day separately.
  const day1Windows = windowsByDay.get(startParts.dayOfWeek);
  if (day1Windows) {
    const s1 = startParts.minutesOfDay;
    if (day1Windows.some((w) => intervalsOverlap(s1, 24 * 60, w.startMinutes, w.endMinutes))) {
      return true;
    }
  }
  const day2Windows = windowsByDay.get(endParts.dayOfWeek);
  if (day2Windows) {
    const e2 = endParts.minutesOfDay;
    if (e2 > 0 && day2Windows.some((w) => intervalsOverlap(0, e2, w.startMinutes, w.endMinutes))) {
      return true;
    }
  }
  return false;
};

/**
 * Total monitored hours between `fromTimestamp` and `toTimestamp` in the
 * property's timezone. Used by the utilisation table to convert a
 * usage-% into `usageTimeHours`.
 *
 * If `monitoringHours` is empty, returns the raw duration (24/7).
 */
export const calculateTotalMonitoredHours = (
  fromTimestamp: string,
  toTimestamp: string,
  monitoringHours: MonitoringHours[],
  propertyTimezone: string,
): number => {
  const start = dayjs(fromTimestamp).tz(propertyTimezone);
  const end = dayjs(toTimestamp).tz(propertyTimezone);
  if (!end.isAfter(start)) return 0;

  if (!monitoringHours.length) {
    return end.diff(start, "minute", true) / 60;
  }

  const windowsByDay = new Map<DayOfWeek, ResolvedWindow[]>();
  for (const w of resolveMonitoringWindows(monitoringHours, propertyTimezone)) {
    if (!windowsByDay.has(w.dayOfWeek)) windowsByDay.set(w.dayOfWeek, []);
    windowsByDay.get(w.dayOfWeek)!.push(w);
  }

  let totalMinutes = 0;
  let cursor = start.startOf("day");
  const endDay = end.startOf("day");

  while (cursor.isBefore(endDay) || cursor.isSame(endDay, "day")) {
    const dayOfWeek = cursor.format(DATE_FORMATS.DAY_FULL).toLowerCase() as DayOfWeek;
    const windows = windowsByDay.get(dayOfWeek) ?? [];

    for (const w of windows) {
      let winStart = w.startMinutes;
      let winEnd = w.endMinutes;

      if (cursor.isSame(start, "day")) {
        winStart = Math.max(winStart, start.hour() * 60 + start.minute());
      }
      if (cursor.isSame(end, "day")) {
        winEnd = Math.min(winEnd, end.hour() * 60 + end.minute());
      }
      if (winEnd > winStart) totalMinutes += winEnd - winStart;
    }

    cursor = cursor.add(1, "day");
  }

  return totalMinutes / 60;
};
